import { defineMessages, type useIntl } from 'react-intl';

import type { PlaceholderIllustration, PlaceholderProps } from './types';

import caughtUpIllustration from '../../assets/illustrations/caught-up.svg';
import chartNoDataIllustration from '../../assets/illustrations/chart-no-data.svg';
import errorIllustration from '../../assets/illustrations/error.svg';
import firstUseIllustration from '../../assets/illustrations/first-use.svg';
import genericIllustration from '../../assets/illustrations/generic.svg';
import noResultIllustration from '../../assets/illustrations/no-result.svg';

const messages = defineMessages({
  caughtUpTitle: {
    id: 'Placeholder.caughtUpTitle',
    defaultMessage: "You're all caught up",
    description: 'Default title for the caught-up placeholder variant',
  },
  chartNoDataTitle: {
    id: 'Placeholder.chartNoDataTitle',
    defaultMessage: 'No data to display',
    description: 'Default title for the chart-no-data placeholder variant',
  },
  errorTitle: {
    id: 'Placeholder.errorTitle',
    defaultMessage: 'Something went wrong',
    description: 'Default title for the error placeholder variant',
  },
  firstUseTitle: {
    id: 'Placeholder.firstUseTitle',
    defaultMessage: 'No item to display',
    description: 'Default title for the first-use placeholder variant',
  },
  noResultTitle: {
    id: 'Placeholder.noResultTitle',
    defaultMessage: 'No matches found',
    description: 'Default title for the no-result placeholder variant',
  },
});

const PRESET_TITLE_MESSAGES = {
  'caught-up': messages.caughtUpTitle,
  'chart-no-data': messages.chartNoDataTitle,
  error: messages.errorTitle,
  'first-use': messages.firstUseTitle,
  'no-result': messages.noResultTitle,
} as const;

const PLACEHOLDER_ILLUSTRATIONS = {
  'caught-up': caughtUpIllustration,
  'chart-no-data': chartNoDataIllustration,
  custom: genericIllustration,
  error: errorIllustration,
  'first-use': firstUseIllustration,
  'no-result': noResultIllustration,
} as const satisfies Record<
  Exclude<PlaceholderIllustration, 'loading'>,
  string
>;

export function resolveIllustrationSrc(illustration: PlaceholderIllustration) {
  if (illustration === 'loading') {
    return null;
  }

  return PLACEHOLDER_ILLUSTRATIONS[illustration];
}

export function resolveDisplayDescription(description?: string) {
  const trimmedDescription = description?.trim();
  return trimmedDescription || undefined;
}

export function resolveDisplayTitle(
  intl: ReturnType<typeof useIntl>,
  {
    illustration,
    isLoading,
    title,
  }: {
    illustration?: PlaceholderProps['illustration'];
    isLoading: boolean;
    title?: string;
  },
) {
  if (isLoading) {
    return undefined;
  }

  const trimmedTitle = title?.trim();
  if (trimmedTitle) {
    return trimmedTitle;
  }

  if (
    !illustration ||
    illustration === 'custom' ||
    illustration === 'loading'
  ) {
    return undefined;
  }

  return intl.formatMessage(PRESET_TITLE_MESSAGES[illustration]);
}

export function resolveTitleVariant({
  isIconMedia,
  size,
}: {
  isIconMedia: boolean;
  size: PlaceholderProps['size'];
}) {
  if (isIconMedia) {
    return 'body-semi-m';
  }

  return size === 's' ? 'heading-xs' : 'heading-s';
}
